import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import { Button } from './ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Separator } from './ui/separator';
import { Input } from './ui/input';
import { Label } from './ui/label';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from './ui/select';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from './ui/dialog';
import {
  Shield, ArrowLeft, Users, Globe, Trash2, Search, Loader2, CheckCircle2,
  Timer, UserCog, Settings, Palette, Server, Save, Pencil, AlertCircle, Eye, EyeOff,
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const RATE_OPTIONS = [
  { hours: 24, label: '24 Hours' },
  { hours: 48, label: '48 Hours' },
  { hours: 72, label: '72 Hours' },
  { hours: 168, label: '7 Days' },
  { hours: 360, label: '15 Days' },
  { hours: 720, label: '30 Days' },
];

const TABS = [
  { id: 'rate-limits', label: 'Rate Limits', icon: Timer },
  { id: 'users', label: 'Users', icon: Users },
  { id: 'settings', label: 'Settings', icon: Settings },
];

const AdminPanel = ({ onLogout }) => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('rate-limits');
  const [loading, setLoading] = useState(true);
  const [users, setUsers] = useState([]);
  const [rateLimits, setRateLimits] = useState({ global: {}, user_limits: [] });
  const [searchQuery, setSearchQuery] = useState('');
  const [globalEnabled, setGlobalEnabled] = useState(false);
  const [globalHours, setGlobalHours] = useState('');
  const [savingGlobal, setSavingGlobal] = useState(false);
  const [userLimitDialog, setUserLimitDialog] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [userLimitHours, setUserLimitHours] = useState('');
  const [savingUserLimit, setSavingUserLimit] = useState(false);
  // Settings
  const [virtSettings, setVirtSettings] = useState({ api_ip: '', api_key: '', api_pass: '', port: '4085' });
  const [branding, setBranding] = useState({ app_title: '', logo_url: '', favicon_url: '', footer_text: '' });
  const [savingSettings, setSavingSettings] = useState(false);
  const [showApiPass, setShowApiPass] = useState(false);
  // Edit user dialog
  const [editUserDialog, setEditUserDialog] = useState(false);
  const [editUser, setEditUser] = useState({});
  const [savingUser, setSavingUser] = useState(false);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [usersRes, limitsRes] = await Promise.all([
        axios.get(`${API}/admin/users`),
        axios.get(`${API}/admin/rate-limits`),
      ]);
      setUsers(usersRes.data);
      setRateLimits(limitsRes.data);
      const g = limitsRes.data.global || {};
      setGlobalEnabled(g.enabled || false);
      setGlobalHours(g.hours ? String(g.hours) : '');
    } catch (error) {
      if (error.response?.status === 403) {
        toast.error('Admin access required');
        navigate('/dashboard');
      }
    } finally { setLoading(false); }
  }, [navigate]);

  const fetchSettings = useCallback(async () => {
    try {
      const [virtRes, brandRes] = await Promise.all([
        axios.get(`${API}/admin/settings/virtualizor`),
        axios.get(`${API}/admin/settings/branding`),
      ]);
      setVirtSettings({ api_ip: virtRes.data.api_ip || '', api_key: virtRes.data.api_key || '', api_pass: virtRes.data.api_pass || '', port: virtRes.data.port || '4085' });
      setBranding({
        app_title: brandRes.data.app_title || '',
        logo_url: brandRes.data.logo_url || '',
        favicon_url: brandRes.data.favicon_url || '',
        footer_text: brandRes.data.footer_text || '',
      });
    } catch (e) { /* ignore */ }
  }, []);

  useEffect(() => { fetchData(); fetchSettings(); }, [fetchData, fetchSettings]);

  const handleSaveGlobal = async () => {
    setSavingGlobal(true);
    try {
      await axios.post(`${API}/admin/rate-limits/global`, {
        enabled: globalEnabled,
        hours: globalEnabled && globalHours ? parseInt(globalHours) : null,
      });
      toast.success(globalEnabled ? 'Global rate limit enabled' : 'Global rate limit disabled');
      fetchData();
    } catch (e) { toast.error('Failed to save'); }
    finally { setSavingGlobal(false); }
  };

  const handleSetUserLimit = async () => {
    if (!selectedUser || !userLimitHours) return;
    setSavingUserLimit(true);
    try {
      await axios.post(`${API}/admin/rate-limits/user`, { user_id: selectedUser.id, hours: parseInt(userLimitHours) });
      toast.success(`Rate limit set for ${selectedUser.email}`);
      setUserLimitDialog(false);
      fetchData();
    } catch (e) { toast.error('Failed'); }
    finally { setSavingUserLimit(false); }
  };

  const handleRemoveUserLimit = async (userId, email) => {
    try {
      await axios.delete(`${API}/admin/rate-limits/user/${userId}`);
      toast.success(`Rate limit removed for ${email}`);
      fetchData();
    } catch (e) { toast.error('Failed'); }
  };

  const handleSaveVirtualizor = async () => {
    setSavingSettings(true);
    try {
      await axios.post(`${API}/admin/settings/virtualizor`, virtSettings);
      toast.success('Virtualizor settings saved');
    } catch (e) { toast.error('Failed'); }
    finally { setSavingSettings(false); }
  };

  const handleSaveBranding = async () => {
    setSavingSettings(true);
    try {
      await axios.post(`${API}/admin/settings/branding`, branding);
      toast.success('Branding saved. Refresh to see changes.');
    } catch (e) { toast.error('Failed'); }
    finally { setSavingSettings(false); }
  };

  const openEditUser = (user) => {
    setEditUser({ id: user.id, name: user.name, email: user.email, virtualizor_uid: user.virtualizor_uid || '', is_admin: user.is_admin || false, password: '' });
    setEditUserDialog(true);
  };

  const handleSaveUser = async () => {
    setSavingUser(true);
    try {
      const payload = {};
      if (editUser.name) payload.name = editUser.name;
      if (editUser.email) payload.email = editUser.email;
      if (editUser.password) payload.password = editUser.password;
      if (editUser.virtualizor_uid !== undefined) payload.virtualizor_uid = editUser.virtualizor_uid;
      if (editUser.is_admin !== undefined) payload.is_admin = editUser.is_admin;
      await axios.put(`${API}/admin/users/${editUser.id}`, payload);
      toast.success('User updated');
      setEditUserDialog(false);
      fetchData();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    finally { setSavingUser(false); }
  };

  const handleDeleteUser = async (userId, email) => {
    if (!window.confirm(`Delete user ${email}? This cannot be undone.`)) return;
    try {
      await axios.delete(`${API}/admin/users/${userId}`);
      toast.success('User deleted');
      fetchData();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const filteredUsers = users.filter(u => {
    if (!searchQuery.trim()) return true;
    const q = searchQuery.toLowerCase();
    return u.email?.toLowerCase().includes(q) || u.name?.toLowerCase().includes(q);
  });

  const getUserLimitInfo = (user) => {
    if (user.rate_limit) return { type: 'custom', label: user.rate_limit.label };
    if (globalEnabled && globalHours) {
      const opt = RATE_OPTIONS.find(o => o.hours === parseInt(globalHours));
      return { type: 'global', label: opt?.label || `${globalHours}h` };
    }
    return { type: 'none', label: 'No limit' };
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900">
        <Loader2 className="h-12 w-12 text-purple-500 animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900">
      <header className="bg-slate-800/50 backdrop-blur border-b border-purple-700">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <Button variant="ghost" size="sm" onClick={() => navigate('/dashboard')} className="text-purple-300 hover:text-white hover:bg-purple-700" data-testid="back-to-dashboard">
                <ArrowLeft className="w-4 h-4 mr-1" />Back
              </Button>
              <div className="flex items-center justify-center w-10 h-10 bg-orange-600 rounded-lg"><Shield className="w-6 h-6 text-white" /></div>
              <div>
                <h1 className="text-xl font-bold text-white">Admin Panel</h1>
                <p className="text-sm text-purple-300">System Management</p>
              </div>
            </div>
          </div>
        </div>
      </header>

      <main className="container mx-auto px-4 py-6">
        {/* Tabs */}
        <div className="flex space-x-1 mb-6 bg-slate-800/50 rounded-lg p-1 w-fit" data-testid="admin-tabs">
          {TABS.map(tab => (
            <button key={tab.id} onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-colors ${activeTab === tab.id ? 'bg-purple-600 text-white' : 'text-purple-300 hover:bg-slate-700'}`}
              data-testid={`tab-${tab.id}`}
            >
              <tab.icon className="w-4 h-4" />{tab.label}
            </button>
          ))}
        </div>

        {/* Rate Limits Tab */}
        {activeTab === 'rate-limits' && (
          <div className="space-y-6">
            <Card className="border-purple-700 bg-slate-800/50 backdrop-blur">
              <CardHeader>
                <div className="flex items-center gap-3">
                  <Globe className="w-6 h-6 text-purple-400" />
                  <div><CardTitle className="text-white">Global Rate Limit</CardTitle>
                    <CardDescription className="text-purple-300">Applies to all users unless they have a custom limit</CardDescription></div>
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center gap-4 flex-wrap">
                  <span className="text-white text-sm">Status:</span>
                  <Button size="sm" onClick={() => setGlobalEnabled(true)} className={globalEnabled ? 'bg-green-600 hover:bg-green-700' : 'bg-transparent border border-purple-600 text-purple-300'} data-testid="global-enable-btn">Enabled</Button>
                  <Button size="sm" onClick={() => setGlobalEnabled(false)} className={!globalEnabled ? 'bg-red-600 hover:bg-red-700' : 'bg-transparent border border-purple-600 text-purple-300'} data-testid="global-disable-btn">Disabled</Button>
                  {globalEnabled && (
                    <Select value={globalHours} onValueChange={setGlobalHours}>
                      <SelectTrigger className="w-48 bg-slate-700 border-purple-600" data-testid="global-rate-select"><SelectValue placeholder="Select duration" /></SelectTrigger>
                      <SelectContent className="bg-slate-700 border-purple-600">{RATE_OPTIONS.map(o => <SelectItem key={o.hours} value={String(o.hours)}>{o.label}</SelectItem>)}</SelectContent>
                    </Select>
                  )}
                  <Button onClick={handleSaveGlobal} disabled={savingGlobal || (globalEnabled && !globalHours)} className="bg-purple-600 hover:bg-purple-700" data-testid="save-global-btn">
                    {savingGlobal ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}Save
                  </Button>
                </div>
                <div className="bg-slate-700/30 rounded-lg p-3 text-sm text-purple-300">
                  {globalEnabled ? <>Users can change IP once every <strong className="text-white">{RATE_OPTIONS.find(o => String(o.hours) === globalHours)?.label || '...'}</strong>. Users with custom limits are excluded.</> : <>Global rate limit is <strong className="text-red-400">disabled</strong>. Users without custom limits have unlimited IP changes.</>}
                </div>
              </CardContent>
            </Card>

            {/* Per-user limits within rate-limits tab */}
            <Card className="border-purple-700 bg-slate-800/50 backdrop-blur">
              <CardHeader>
                <div className="flex items-center gap-3"><UserCog className="w-6 h-6 text-purple-400" /><div><CardTitle className="text-white">Per-User Limits</CardTitle><CardDescription className="text-purple-300">Override global limit for specific users</CardDescription></div></div>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="relative">
                  <Search className="absolute left-3 top-2.5 h-4 w-4 text-purple-400" />
                  <Input placeholder="Search users..." value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} className="pl-9 bg-slate-700/50 border-purple-600 text-white placeholder:text-purple-400" />
                </div>
                <div className="space-y-2 max-h-[400px] overflow-y-auto">
                  {filteredUsers.map(user => {
                    const li = getUserLimitInfo(user);
                    return (
                      <div key={user.id} className="flex items-center justify-between p-3 bg-slate-700/30 rounded-lg border border-purple-700/50">
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-2"><span className="text-white font-medium truncate">{user.name}</span>
                            {(user.is_admin || user.email === 'admin@hosterdaddy.com') && <Badge className="bg-orange-600 text-white text-[10px] px-1.5">Admin</Badge>}</div>
                          <div className="text-sm text-purple-300 truncate">{user.email}</div>
                        </div>
                        <div className="flex items-center gap-2 ml-3">
                          {li.type === 'custom' ? <Badge className="bg-blue-600 text-white"><Timer className="w-3 h-3 mr-1" />{li.label}</Badge> : li.type === 'global' ? <Badge className="bg-slate-600 text-purple-200"><Globe className="w-3 h-3 mr-1" />{li.label}</Badge> : <Badge className="bg-slate-600 text-green-300"><CheckCircle2 className="w-3 h-3 mr-1" />No limit</Badge>}
                          <Button size="sm" onClick={() => { setSelectedUser(user); setUserLimitHours(user.rate_limit?.hours ? String(user.rate_limit.hours) : ''); setUserLimitDialog(true); }} className="bg-purple-600 hover:bg-purple-700">Set</Button>
                          {li.type === 'custom' && <Button size="sm" variant="outline" onClick={() => handleRemoveUserLimit(user.id, user.email)} className="border-red-600 text-red-400 hover:bg-red-600 hover:text-white"><Trash2 className="w-3 h-3" /></Button>}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Users Tab */}
        {activeTab === 'users' && (
          <Card className="border-purple-700 bg-slate-800/50 backdrop-blur">
            <CardHeader>
              <div className="flex items-center gap-3"><Users className="w-6 h-6 text-purple-400" /><div><CardTitle className="text-white">Registered Users</CardTitle><CardDescription className="text-purple-300">Manage all users, edit details, link Virtualizor UID, grant admin access</CardDescription></div></div>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="relative">
                <Search className="absolute left-3 top-2.5 h-4 w-4 text-purple-400" />
                <Input placeholder="Search users..." value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} className="pl-9 bg-slate-700/50 border-purple-600 text-white placeholder:text-purple-400" data-testid="admin-user-search" />
              </div>
              <div className="space-y-2 max-h-[500px] overflow-y-auto">
                {filteredUsers.map(user => (
                  <div key={user.id} className="flex items-center justify-between p-4 bg-slate-700/30 rounded-lg border border-purple-700/50" data-testid={`admin-user-${user.id}`}>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="text-white font-medium">{user.name}</span>
                        {(user.is_admin || user.email === 'admin@hosterdaddy.com') && <Badge className="bg-orange-600 text-white text-[10px] px-1.5">Admin</Badge>}
                        {user.email === 'admin@hosterdaddy.com' && <Badge className="bg-red-600 text-white text-[10px] px-1.5">Master</Badge>}
                      </div>
                      <div className="text-sm text-purple-300">{user.email}</div>
                      <div className="text-xs text-purple-400 mt-1">Virtualizor UID: {user.virtualizor_uid || 'Not linked'} | Registered: {new Date(user.created_at).toLocaleDateString()}</div>
                    </div>
                    <div className="flex items-center gap-2 ml-3">
                      <Button size="sm" onClick={() => openEditUser(user)} className="bg-purple-600 hover:bg-purple-700" data-testid={`edit-user-${user.id}`}>
                        <Pencil className="w-3.5 h-3.5 mr-1" />Edit
                      </Button>
                      {user.email !== 'admin@hosterdaddy.com' && (
                        <Button size="sm" variant="outline" onClick={() => handleDeleteUser(user.id, user.email)} className="border-red-600 text-red-400 hover:bg-red-600 hover:text-white">
                          <Trash2 className="w-3.5 h-3.5" />
                        </Button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Settings Tab */}
        {activeTab === 'settings' && (
          <div className="space-y-6">
            {/* Virtualizor Settings */}
            <Card className="border-purple-700 bg-slate-800/50 backdrop-blur">
              <CardHeader>
                <div className="flex items-center gap-3"><Server className="w-6 h-6 text-purple-400" /><div><CardTitle className="text-white">Virtualizor Connection</CardTitle><CardDescription className="text-purple-300">API credentials for your Virtualizor server</CardDescription></div></div>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="text-purple-200">Server IP</Label>
                    <Input value={virtSettings.api_ip} onChange={(e) => setVirtSettings(s => ({...s, api_ip: e.target.value}))} className="bg-slate-700 border-purple-600 text-white" placeholder="195.201.60.179" data-testid="virt-ip-input" />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-purple-200">API Key</Label>
                    <Input value={virtSettings.api_key} onChange={(e) => setVirtSettings(s => ({...s, api_key: e.target.value}))} className="bg-slate-700 border-purple-600 text-white" placeholder="Enter API key" data-testid="virt-key-input" />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-purple-200">API Password</Label>
                    <div className="relative">
                      <Input type={showApiPass ? "text" : "password"} value={virtSettings.api_pass} onChange={(e) => setVirtSettings(s => ({...s, api_pass: e.target.value}))} className="bg-slate-700 border-purple-600 text-white pr-10" placeholder="Enter API password" data-testid="virt-pass-input" />
                      <button type="button" onClick={() => setShowApiPass(!showApiPass)} className="absolute right-3 top-2.5 text-purple-400 hover:text-white transition-colors" data-testid="toggle-pass-visibility">
                        {showApiPass ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </button>
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label className="text-purple-200">Port</Label>
                    <Input value={virtSettings.port} onChange={(e) => setVirtSettings(s => ({...s, port: e.target.value}))} className="bg-slate-700 border-purple-600 text-white" placeholder="4085" data-testid="virt-port-input" />
                  </div>
                </div>
                <Button onClick={handleSaveVirtualizor} disabled={savingSettings} className="bg-purple-600 hover:bg-purple-700" data-testid="save-virt-btn">
                  {savingSettings ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}Save Virtualizor Settings
                </Button>
              </CardContent>
            </Card>

            {/* Branding Settings */}
            <Card className="border-purple-700 bg-slate-800/50 backdrop-blur">
              <CardHeader>
                <div className="flex items-center gap-3"><Palette className="w-6 h-6 text-purple-400" /><div><CardTitle className="text-white">Branding</CardTitle><CardDescription className="text-purple-300">Customize the look of your app</CardDescription></div></div>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="text-purple-200">App Title</Label>
                    <Input value={branding.app_title} onChange={(e) => setBranding(b => ({...b, app_title: e.target.value}))} className="bg-slate-700 border-purple-600 text-white" placeholder="VPS IP Manager" data-testid="brand-title-input" />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-purple-200">Footer Text</Label>
                    <Input value={branding.footer_text} onChange={(e) => setBranding(b => ({...b, footer_text: e.target.value}))} className="bg-slate-700 border-purple-600 text-white" placeholder="Powered by HosterDaddy" data-testid="brand-footer-input" />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-purple-200">Logo URL</Label>
                    <Input value={branding.logo_url} onChange={(e) => setBranding(b => ({...b, logo_url: e.target.value}))} className="bg-slate-700 border-purple-600 text-white" placeholder="https://example.com/logo.png" data-testid="brand-logo-input" />
                    {branding.logo_url && <img src={branding.logo_url} alt="Logo preview" className="h-10 mt-1 rounded" onError={(e) => e.target.style.display='none'} />}
                  </div>
                  <div className="space-y-2">
                    <Label className="text-purple-200">Favicon URL</Label>
                    <Input value={branding.favicon_url} onChange={(e) => setBranding(b => ({...b, favicon_url: e.target.value}))} className="bg-slate-700 border-purple-600 text-white" placeholder="https://example.com/favicon.ico" data-testid="brand-favicon-input" />
                    {branding.favicon_url && <img src={branding.favicon_url} alt="Favicon preview" className="h-6 mt-1" onError={(e) => e.target.style.display='none'} />}
                  </div>
                </div>
                <Button onClick={handleSaveBranding} disabled={savingSettings} className="bg-purple-600 hover:bg-purple-700" data-testid="save-brand-btn">
                  {savingSettings ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}Save Branding
                </Button>
              </CardContent>
            </Card>
          </div>
        )}
      </main>

      {/* User Rate Limit Dialog */}
      <Dialog open={userLimitDialog} onOpenChange={setUserLimitDialog}>
        <DialogContent className="bg-slate-800 border-purple-700 text-white">
          <DialogHeader><DialogTitle>Set Rate Limit</DialogTitle><DialogDescription className="text-purple-300">For {selectedUser?.email}</DialogDescription></DialogHeader>
          <div className="py-4">
            <Select value={userLimitHours} onValueChange={setUserLimitHours}>
              <SelectTrigger className="bg-slate-700 border-purple-600"><SelectValue placeholder="Select duration" /></SelectTrigger>
              <SelectContent className="bg-slate-700 border-purple-600">{RATE_OPTIONS.map(o => <SelectItem key={o.hours} value={String(o.hours)}>{o.label}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setUserLimitDialog(false)} className="border-purple-600 text-purple-300">Cancel</Button>
            <Button onClick={handleSetUserLimit} disabled={savingUserLimit || !userLimitHours} className="bg-purple-600 hover:bg-purple-700">
              {savingUserLimit ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit User Dialog */}
      <Dialog open={editUserDialog} onOpenChange={setEditUserDialog}>
        <DialogContent className="bg-slate-800 border-purple-700 text-white">
          <DialogHeader><DialogTitle>Edit User</DialogTitle><DialogDescription className="text-purple-300">{editUser.email}</DialogDescription></DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label className="text-purple-200">Name</Label>
              <Input value={editUser.name || ''} onChange={(e) => setEditUser(u => ({...u, name: e.target.value}))} className="bg-slate-700 border-purple-600 text-white" />
            </div>
            <div className="space-y-2">
              <Label className="text-purple-200">Email</Label>
              <Input value={editUser.email || ''} onChange={(e) => setEditUser(u => ({...u, email: e.target.value}))} className="bg-slate-700 border-purple-600 text-white" />
            </div>
            <div className="space-y-2">
              <Label className="text-purple-200">New Password (leave empty to keep)</Label>
              <Input type="password" value={editUser.password || ''} onChange={(e) => setEditUser(u => ({...u, password: e.target.value}))} className="bg-slate-700 border-purple-600 text-white" placeholder="Leave empty to keep current" />
            </div>
            <div className="space-y-2">
              <Label className="text-purple-200">Virtualizor User ID</Label>
              <Input value={editUser.virtualizor_uid || ''} onChange={(e) => setEditUser(u => ({...u, virtualizor_uid: e.target.value}))} className="bg-slate-700 border-purple-600 text-white" placeholder="e.g. 1360" />
            </div>
            <div className="flex items-center gap-3">
              <Label className="text-purple-200">Admin Access</Label>
              <Button size="sm" onClick={() => setEditUser(u => ({...u, is_admin: !u.is_admin}))}
                className={editUser.is_admin ? 'bg-green-600 hover:bg-green-700' : 'bg-slate-600 hover:bg-slate-500'}
                disabled={editUser.email === 'admin@hosterdaddy.com'}
              >
                {editUser.is_admin ? 'Admin: ON' : 'Admin: OFF'}
              </Button>
              {editUser.email === 'admin@hosterdaddy.com' && <span className="text-xs text-red-400">Master admin cannot be changed</span>}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditUserDialog(false)} className="border-purple-600 text-purple-300">Cancel</Button>
            <Button onClick={handleSaveUser} disabled={savingUser} className="bg-purple-600 hover:bg-purple-700" data-testid="save-user-btn">
              {savingUser ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminPanel;
